/**
 * SplitPanel
 * Shared drag-resize utility for two-column flex layouts.
 * Configurable via options object; persists ratio in sessionStorage.
 */
var SplitPanel = (function() {
  'use strict';

  function SplitPanel(options) {
    var opts = options || {};
    this.minWidth = opts.minWidth || 280;
    this.storageKey = opts.storageKey || 'splitPanelRatio';
    this.getContainer = opts.getContainer || function() { return null; };
    this.getLeft = opts.getLeft || function() { return null; };
    this.getRight = opts.getRight || function() { return null; };
    this.getHandle = opts.getHandle || function() { return null; };
    this.onDragStart = opts.onDragStart || null;
    this.onDragEnd = opts.onDragEnd || null;
    this.collapseBelowMin = opts.collapseBelowMin !== false;

    this.initialized = false;
    this._handleMousedown = null;
    this._handleTouchstart = null;
    this._handleResize = null;
  }

  SplitPanel.prototype.getContainerWidth = function() {
    var c = this.getContainer();
    return c ? c.offsetWidth : 0;
  };

  SplitPanel.prototype.getRatioFromStorage = function() {
    try {
      var val = sessionStorage.getItem(this.storageKey);
      var ratio = parseFloat(val);
      if (!isNaN(ratio) && ratio > 0.1 && ratio < 0.9) return ratio;
    } catch (e) {}
    return 0.5;
  };

  SplitPanel.prototype.saveRatio = function(ratio) {
    try {
      sessionStorage.setItem(this.storageKey, String(ratio));
    } catch (e) {}
  };

  SplitPanel.prototype.computeRatio = function(clientX) {
    var container = this.getContainer();
    if (!container) return 0.5;

    var handle = this.getHandle();
    var handleW = handle ? handle.offsetWidth : 8;
    var rect = container.getBoundingClientRect();
    var available = container.offsetWidth - handleW;
    var offsetX = clientX - rect.left;
    var ratio = offsetX / available;
    return Math.max(0.15, Math.min(0.85, ratio));
  };

  SplitPanel.prototype.applyRatio = function(ratio) {
    var left = this.getLeft();
    var right = this.getRight();
    var handle = this.getHandle();
    var containerW = this.getContainerWidth();
    if (!left || !right || !handle || !containerW) return;

    var handleW = handle.offsetWidth || 8;
    var available = containerW - handleW;

    if (this.collapseBelowMin && available < this.minWidth * 2) {
      left.style.flex = '1 1 100%';
      right.style.flex = '1 1 100%';
      handle.style.display = 'none';
      left.style.minWidth = '0';
      right.style.minWidth = '0';
      return;
    }

    handle.style.display = '';
    left.style.minWidth = this.minWidth + 'px';
    right.style.minWidth = this.minWidth + 'px';
    var leftW = Math.max(this.minWidth, Math.round(available * ratio));
    var rightW = Math.max(this.minWidth, available - leftW);

    left.style.flex = '0 0 ' + leftW + 'px';
    right.style.flex = '0 0 ' + rightW + 'px';
  };

  SplitPanel.prototype.onPointerDown = function(e) {
    e.preventDefault();
    var self = this;
    var container = this.getContainer();
    var handle = this.getHandle();
    if (!container || !handle) return;

    if (this.onDragStart) this.onDragStart();

    function onPointerMove(ev) {
      ev.preventDefault();
      var ratio = self.computeRatio(ev.clientX);
      self.applyRatio(ratio);
    }

    function onPointerUp(ev) {
      var ratio = self.computeRatio(ev.clientX);
      self.saveRatio(ratio);
      if (self.onDragEnd) self.onDragEnd();
      document.removeEventListener('mousemove', onPointerMove);
      document.removeEventListener('mouseup', onPointerUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    }

    function onTouchMove(ev) {
      ev.preventDefault();
      if (ev.touches.length > 0) {
        var ratio = self.computeRatio(ev.touches[0].clientX);
        self.applyRatio(ratio);
      }
    }

    function onTouchEnd(ev) {
      if (ev.changedTouches.length > 0) {
        var ratio = self.computeRatio(ev.changedTouches[0].clientX);
        self.saveRatio(ratio);
      }
      if (self.onDragEnd) self.onDragEnd();
      document.removeEventListener('mousemove', onPointerMove);
      document.removeEventListener('mouseup', onPointerUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    }

    if (e.type === 'touchstart') {
      document.addEventListener('touchmove', onTouchMove, { passive: false });
      document.addEventListener('touchend', onTouchEnd);
    } else {
      document.addEventListener('mousemove', onPointerMove);
      document.addEventListener('mouseup', onPointerUp);
    }
  };

  SplitPanel.prototype.reset = function() {
    if (this.initialized) {
      var handle = this.getHandle();
      if (handle) {
        if (this._handleMousedown) handle.removeEventListener('mousedown', this._handleMousedown);
        if (this._handleTouchstart) handle.removeEventListener('touchstart', this._handleTouchstart);
      }
      if (this._handleResize) window.removeEventListener('resize', this._handleResize);
    }
    this.initialized = false;
    this._handleMousedown = null;
    this._handleTouchstart = null;
    this._handleResize = null;
  };

  SplitPanel.prototype.init = function() {
    if (this.initialized) return;
    this.initialized = true;

    var self = this;
    var handle = this.getHandle();
    if (handle) {
      this._handleMousedown = function(e) { self.onPointerDown(e); };
      handle.addEventListener('mousedown', this._handleMousedown);

      this._handleTouchstart = function(e) {
        e.preventDefault();
        self.onPointerDown(e);
      };
      handle.addEventListener('touchstart', this._handleTouchstart);
    }

    var ratio = this.getRatioFromStorage();
    this.applyRatio(ratio);

    this._handleResize = function() {
      var r = self.getRatioFromStorage();
      self.applyRatio(r);
    };
    window.addEventListener('resize', this._handleResize);
  };

  return SplitPanel;
})();

if (typeof window !== 'undefined') {
  window.SplitPanel = SplitPanel;
}
